import React, { useState, useMemo, useEffect } from "react";
import { Col, Row, Spinner, Dropdown } from "react-bootstrap";
import { Empty, Input, Select } from "antd";
import { toast } from "react-toastify";

// Layout & Table Components
import AdvanceTable from "components/common/advance-table/AdvanceTable";
import AdvanceTableProvider from "providers/AdvanceTableProvider";
import useAdvanceTable from "hooks/useAdvanceTable";
import CardDropdown from "components/common/CardDropdown";
import IconButton from "components/common/IconButton";
import WidgetSectionTitle from "widgets/WidgetSectionTitle";
import TablePaginationFooter from "components/common/TablePaginationFooter";
import { ConfirmDeleteModal } from "components/common/ConfirmDeleteModal";

// Security Question Specifics
import useSecurityQuestions from "hooks/accountManagement/useSecurityQuestions";
import AddSecurityQuestionModal from "./addModal";
import ViewSecurityQuestionModal from "./viewModal";
import EditSecurityQuestionModal from "./editModal";

// Access Control
import Guard from "components/access-control/rbac/Guard";
import { PRIV } from "components/access-control/rbac/privileges";

const SecurityQnsTable = () => {
  // State for Pagination, Search and Filter
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("ALL");

  const {
    securityQns = [],
    loading,
    fetchSecurityQuestions,
    addSecurityQuestion,
    updateSecurityQuestion,
    deleteSecurityQuestion,
    error,
  } = useSecurityQuestions();

  // Modal States
  const [showAddModal, setShowAddModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [questionToDelete, setQuestionToDelete] = useState(null);

  const isSearchRequest = searchTerm.trim().length >= 3;

  const handleDelete = async () => {
    try {
      await deleteSecurityQuestion(questionToDelete);
      setShowDeleteModal(false);
      setQuestionToDelete(null);
    } catch (err) {
      toast.error("Failed to delete question");
    }
  };

  // ================= FILTER LOGIC =================
  const filteredQuestions = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    const hasSearch = term.length >= 3;

    return (securityQns || []).filter((item) => {
      const status = item?.active === true ? "ACTIVE" : "INACTIVE";
      const byStatus = selectedStatus === "ALL" || selectedStatus === status;

      if (!hasSearch) return byStatus;

      const haystack = [item?.id, item?.questionText, status]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return byStatus && haystack.includes(term);
    });
  }, [securityQns, searchTerm, selectedStatus]);

  const totalElements = filteredQuestions.length;
  const totalPages = totalElements > 0 ? Math.ceil(totalElements / pageSize) : 0;

  // ================= PAGINATION LOGIC =================
  const paginatedQuestions = useMemo(() => {
    const start = page * pageSize;
    return filteredQuestions.slice(start, start + pageSize);
  }, [filteredQuestions, page, pageSize]);

  // ================= COLUMN DEFINITIONS =================
  const columns = useMemo(() => [
    {
      accessorKey: "id",
      header: "ID",
      meta: { 
        headerProps: { 
          className: "text-900 fw-bold",
          style: { width: '150px' } // Adjusted width for truncated ID
        } 
      },
      cell: ({ getValue }) => {
        const id = getValue() || "";
        // Shows first 8 chars, adds dots, and hides the last 6
        const displayId = id.length > 14 
          ? `${id.slice(0, 8)}...${id.slice(-12, -6).replace(/./g, '*')}` 
          : id;
          
        // Alternatively, a simpler "Prefix + ..." approach:
        const simpleTruncate = id.length > 10 ? `${id.slice(0, id.length - 6)}...` : id;

        return (
          <span 
            className="text-900 font-monospace fw-normal" 
            style={{ fontSize: 13, cursor: 'help' }}
            title={id} // Full ID visible on hover
          >
            {id.length > 10 ? `${id.substring(0, id.length - 6)}...` : id}
          </span>
        );
      },
    },
    {
      accessorKey: "questionText",
      header: "Question Text",
      meta: { 
        headerProps: { 
          className: "text-900 fw-bold",
          style: { width: '200px' } // Reduced width for question
        } 
      },
      cell: ({ getValue }) => (
        <div 
          className="text-900 fw-normal text-truncate" 
          title={getValue()} 
          style={{ 
            fontSize: 13, 
            cursor: 'help', 
            maxWidth: '200px' 
          }}
        >
          {getValue()}
        </div>
      ),
    },
    {
      accessorKey: "active",
      header: "Status",
      meta: { 
        headerProps: { 
          className: "text-center text-900 fw-bold",
          style: { width: '100px' } 
        },
        cellProps: { className: "text-center" } 
      },
      cell: ({ getValue }) => {
        const isActive = getValue();
        const style = {
          backgroundColor: isActive ? "#e8f7e3" : "#fbe9e7",
          color: isActive ? "#2e7d32" : "#c62828",
          padding: "2px 8px",
          borderRadius: "4px",
          fontSize: "0.7rem",
          lineHeight: 1.2,
          fontWeight: 600,
          border: isActive ? "1px solid #d5ebd0" : "1px solid #f5c6cb",
          display: "inline-block",
          textTransform: "uppercase"
        };
        return <span style={style}>{isActive ? "ACTIVE" : "INACTIVE"}</span>;
      },
    },
    {
      id: "actions",
      header: "Actions",
      meta: {
        headerProps: { 
          className: "text-center text-900 fw-bold",
          style: { width: '80px' } 
        },
        cellProps: { className: "text-center" },
      },
      cell: ({ row }) => (
        <CardDropdown drop="start">
          <div className="py-2 fw-normal">
            <Dropdown.Item onClick={() => { setSelectedQuestion(row.original); setShowViewModal(true); }}>
              View
            </Dropdown.Item>
            <Guard privilege={PRIV.UPDATE_QUESTIONS}>
              <Dropdown.Item onClick={() => { setSelectedQuestion(row.original); setShowUpdateModal(true); }}>
                Edit
              </Dropdown.Item>
            </Guard>
            <Dropdown.Divider />
            <Guard privilege={PRIV.DELETE_QUESTIONS}>
              <Dropdown.Item 
                className="text-danger" 
                onClick={() => { setQuestionToDelete(row.original.id); setSelectedQuestion(row.original); setShowDeleteModal(true); }}
              >
                Delete
              </Dropdown.Item>
            </Guard>
          </div>
        </CardDropdown>
      ),
    },
  ], []);

  const table = useAdvanceTable({
    data: paginatedQuestions,
    columns,
    sortable: true,
    pagination: false,
  });

  return (
    <>
      <WidgetSectionTitle
        icon="question-circle"
        title="Security Questions"
        subtitle="Manage security questions for user accounts"
        className="mb-3 mt-3"
      />

      <Row className="mb-3">
        <Col className="d-flex justify-content-end">
          <IconButton
            variant="falcon-default"
            size="sm"
            icon="plus"
            onClick={() => setShowAddModal(true)}
            style={{ backgroundColor: "#10b981", color: "#fff", borderColor: "#10b981", fontWeight: 600 }}
          >
            New Security Question
          </IconButton>
        </Col>
      </Row>

  {/* ================= SEARCH & FILTER ROW ================= */}
      <Row className="mb-2 g-3 align-items-center">
        <Col md={8} lg={9}>
          <div className="d-flex align-items-center gap-3 flex-wrap">
            <Input
              prefix={<span style={{ fontSize: 14 }}>🔍</span>}
              placeholder="Search by question text or ID..."
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setPage(0); }}
              allowClear
              size="large"
              style={{ borderRadius: 12, height: 30, flex: "1 1 300px", maxWidth: 300 }}
            />
            <Select
              size="large"
              value={selectedStatus}
              onChange={(val) => { setSelectedStatus(val); setPage(0); }}
              options={[
                { label: "🌍 ALL STATUS", value: "ALL" },
                { label: "✅ ACTIVE", value: "ACTIVE" },
                { label: "🚫 INACTIVE", value: "INACTIVE" },
              ]}
              style={{ minWidth: 180, height: 28 }}
            />
          </div>
        </Col>
        <Col md={4} lg={3} className="text-end">
          <div style={{ fontSize: 13, color: "#64748b" }}>
            Results: <span className="fw-semibold text-dark">{totalElements}</span> of <span className="fw-semibold text-dark">{securityQns.length}</span>
          </div>
        </Col>
      </Row>

      <AdvanceTableProvider {...table}>
        {loading && totalElements === 0 ? (
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 200 }}>
            <Spinner animation="border" variant="success" />
          </div>
        ) : totalElements === 0 ? (
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 200 }}>
            <Empty description="No security questions found" />
          </div>
        ) : (
          <>
            <AdvanceTable
              headerClassName="bg-light text-nowrap align-middle text-dark"
              rowClassName="align-middle white-space-nowrap"
              tableProps={{ bordered: true, striped: true, className: "fs-10 mb-0 overflow-hidden" }}
            />
            <div className="mt-3">
              <TablePaginationFooter
                page={page}
                totalPages={totalPages}
                totalElements={totalElements}
                onPrev={page > 0 ? () => setPage(page - 1) : undefined}
                onNext={page < totalPages - 1 ? () => setPage(page + 1) : undefined}
                pageSize={pageSize}
                onPageSizeChange={(size) => { setPageSize(size); setPage(0); }}
                pageSizeOptions={[10, 20, 30, 50, 100]}
              />
            </div>
          </>
        )}
      </AdvanceTableProvider>

      {/* Modals */}
      <AddSecurityQuestionModal
        show={showAddModal}
        onClose={() => setShowAddModal(false)}
        addSecurityQuestion={addSecurityQuestion}
        fetchSecurityQuestions={fetchSecurityQuestions}
      />
      <ViewSecurityQuestionModal
        show={showViewModal}
        onClose={() => { setShowViewModal(false); setSelectedQuestion(null); }}
        question={selectedQuestion}
        loading={loading}
      />
      <EditSecurityQuestionModal
        show={showUpdateModal}
        onClose={() => { setShowUpdateModal(false); setSelectedQuestion(null); }}
        selectedQuestion={selectedQuestion}
        updateSecurityQuestion={updateSecurityQuestion}
        fetchSecurityQuestions={fetchSecurityQuestions}
      />
      <ConfirmDeleteModal
        show={showDeleteModal}
        setShow={setShowDeleteModal}
        itemName={selectedQuestion?.questionText || "security question"}
        onConfirm={handleDelete}
        loading={loading}
      />
    </>
  );
};

export default SecurityQnsTable;